import { MerchantTradeDiscount } from "@/Apollo/schema";
import {
  DASHBOARD_DISMISS_DAYS,
  DASHBOARD_DISMISS_MAX,
  DASHBOARD_DISMISS_STORAGE_PREFIX,
  TRADE_DISCOUNT_STEPS,
  TradeDiscountStep,
} from "@/features/pro-buyer-discount/static/pro-buyer-discount.static";
import { ApolloError } from "@apollo/client";

export const isTradeDiscountStep = (
  value: number | null | undefined
): value is TradeDiscountStep =>
  value != null && (TRADE_DISCOUNT_STEPS as readonly number[]).includes(value);

export type DashboardDismissRecord = {
  /** How many times the seller pressed "Verberg". */
  count: number;
  /** ISO timestamp of the last "Verberg". */
  dismissedAt: string;
};

/**
 * First visit: show. After the first "Verberg": hidden for 30 days, then shown
 * once more. After the second "Verberg": never again.
 */
export const shouldShowDashboardAction = (
  record: DashboardDismissRecord | null,
  now: Date
): boolean => {
  if (!record || record.count <= 0) {
    return true;
  }

  if (record.count >= DASHBOARD_DISMISS_MAX) {
    return false;
  }

  const dismissedAt = new Date(record.dismissedAt).getTime();

  if (Number.isNaN(dismissedAt)) {
    return true;
  }

  const elapsedDays = (now.getTime() - dismissedAt) / (24 * 60 * 60 * 1000);

  return elapsedDays >= DASHBOARD_DISMISS_DAYS;
};

export const nextDashboardDismissRecord = (
  record: DashboardDismissRecord | null,
  now: Date
): DashboardDismissRecord => ({
  count: (record?.count ?? 0) + 1,
  dismissedAt: now.toISOString(),
});

/** Per merchant, in the browser, through `useStorage("local")`. */
export const dashboardDismissStorageKey = (merchantId: string) =>
  `${DASHBOARD_DISMISS_STORAGE_PREFIX}${merchantId}`;

/** A stored value of the wrong shape counts as "never dismissed". */
export const parseDashboardDismiss = (
  value: unknown
): DashboardDismissRecord | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const { count, dismissedAt } = value as Partial<DashboardDismissRecord>;

  if (typeof count !== "number" || typeof dismissedAt !== "string") {
    return null;
  }

  return { count, dismissedAt };
};

/** Readable gateway error codes the mutation can answer with. */
export type TradeDiscountErrorCode =
  | "INVALID_TRADE_DISCOUNT"
  | "NOT_BUSINESS_ACCOUNT"
  | "FEATURE_DISABLED";

export type SaveTradeDiscountResult =
  | { ok: true; discount: MerchantTradeDiscount }
  | { ok: false; code: TradeDiscountErrorCode | "TRANSPORT" };

const TRADE_DISCOUNT_ERROR_CODES: readonly string[] = [
  "INVALID_TRADE_DISCOUNT",
  "NOT_BUSINESS_ACCOUNT",
  "FEATURE_DISABLED",
];

const isErrorCode = (value: unknown): value is TradeDiscountErrorCode =>
  typeof value === "string" && TRADE_DISCOUNT_ERROR_CODES.includes(value);

/** The first known `extensions.code` of a rejected mutation, else TRANSPORT. */
export const tradeDiscountErrorCode = (
  error: unknown
): TradeDiscountErrorCode | "TRANSPORT" => {
  if (!(error instanceof ApolloError)) {
    return "TRANSPORT";
  }

  const code = error.graphQLErrors
    .map(graphQLError => graphQLError.extensions?.code)
    .find(isErrorCode);

  return code ?? "TRANSPORT";
};
